import type { ChainSigner, SigningItemStatus, SigningListItem } from "./types";

/**
 * Pure list logic for the Signing page: status derivation, filtering, search
 * and sorting. Kept free of React and Supabase so it can be unit-tested.
 */

/** The `signing_sessions.status` CHECK values. */
export type SigningSessionStatus =
  | "creating"
  | "awaiting_user"
  | "completed"
  | "cancelled"
  | "failed"
  | "expired";

/**
 * The positions still to sign — `min(position)` over the rows that are still
 * `pending`, which is the backend's own turn rule (`dokobit-sign-open` step 4).
 * `null` once nobody is pending.
 *
 * Equal positions are a parallel group, so this identifies a SET of signers,
 * never one person.
 */
export function currentPosition(signers: ChainSigner[]): number | null {
  const pending = signers.filter((s) => s.status === "pending");
  return pending.length === 0 ? null : Math.min(...pending.map((s) => s.position));
}

/** The signers whose turn it is right now — the whole parallel group. */
export function currentSigners(signers: ChainSigner[]): ChainSigner[] {
  const position = currentPosition(signers);
  return position === null
    ? []
    : signers.filter((s) => s.status === "pending" && s.position === position);
}

/** True when the viewer is one of the signers holding the current turn. */
export function isWaitingForYou(signers: ChainSigner[]): boolean {
  return currentSigners(signers).some((s) => s.isYou);
}

/**
 * "2 of 3 signed". `removed` signers (dropped by a cancel or an expiry) are
 * excluded from both halves — they are no longer part of what the chain is
 * waiting for, so counting them would make a finished chain read "2 of 3".
 */
export function signerProgress(signers: ChainSigner[]): { signed: number; total: number } {
  const active = signers.filter((s) => s.status !== "removed");
  return { signed: active.filter((s) => s.status === "signed").length, total: active.length };
}

/** One step of the stepper: everybody sharing a position, in list order. */
export interface ChainStep {
  position: number;
  signers: ChainSigner[];
}

/**
 * The chain as steps. Signers sharing a position are ONE step — they may sign
 * in any order and the chain advances only once all of them have — so the
 * stepper must never render them as consecutive steps.
 *
 * Insertion-ordered by first appearance, which the repository has already
 * sorted by position then name.
 */
export function chainSteps(signers: ChainSigner[]): ChainStep[] {
  const byPosition = new Map<number, ChainSigner[]>();
  for (const signer of signers) {
    const group = byPosition.get(signer.position);
    if (group) group.push(signer);
    else byPosition.set(signer.position, [signer]);
  }
  return [...byPosition.entries()].map(([position, group]) => ({ position, signers: group }));
}

/**
 * True when the chain has more than one *position* — i.e. somebody has to wait
 * for somebody else. Two signers sharing position 1 are a parallel group and
 * sign in any order, so a signer count is the wrong test.
 */
export function isOrderedChain(signers: ChainSigner[]): boolean {
  return new Set(signers.map((s) => s.position)).size > 1;
}

/**
 * Map a signing session row onto a list status.
 *
 * Chaining widened the `signing_sessions` SELECT policy past "owner only"
 * (FDP migration 20260804065406), so an in-flight session is no longer
 * necessarily *yours* to act on: it is `awaiting_you` only while the VIEWER
 * holds the current turn, and `awaiting_others` otherwise. Never derive it from
 * the session status alone — under the widened policy that would tell every
 * colleague "Waiting for you" about somebody else's chain.
 *
 * A legacy session predating chaining has NO signer rows at all: the sender
 * signs it themselves, so it waits on the sender and on nobody else. Hence
 * `isSender`, which is the whole viewer test in that case.
 *
 * An open session past `expires_at` reads as expired — Dokobit has already
 * dropped it, and nothing writes that status back to us. A status value we
 * don't recognise maps to "unknown" rather than to the actionable
 * "waiting for you".
 *
 * An open session that nobody is pending on is `finalizing`, NOT "waiting" for
 * anyone: Dokobit records a signature before it assembles the container, so the
 * backend holds the session at `awaiting_user` until `status.file` exists.
 * Without this branch a solo signing reads "Waiting for others" for the whole
 * window between the signature and the container.
 *
 * "Nobody pending" is most of the test, not "everybody signed" — a `rejected`
 * signer is done with the chain too, and {@link currentPosition} steps over them
 * exactly as the backend's turn rule does. It is also the same test the sign
 * dialog makes (`remainingSignerCount === 0`), and the two must not disagree
 * about the same session.
 *
 * But "nobody pending" alone would also catch a chain that ended in DECLINES —
 * a solo signer who refused, or every signer refusing — and "Every signature is
 * in. Sealing the signed container" about a signing with zero signatures is
 * simply false. Worse, this branch sits ahead of the expiry check, so such a row
 * would never fall through to `expired` either, and `finalizing` is not
 * in-flight (SigningDetailPanel), so the sender would lose the Cancel button on
 * it. At least one `signed` signer is therefore required; the dialog's own test
 * is equivalent, since it only asks the question once the viewer's own row is
 * signed (`isYourPartDone`).
 *
 * The legacy shape (no signer rows at all) is out of scope here and stays
 * `awaiting_you` for its sender until the backend completes it: nothing on the
 * session row says whether that sender has already signed, so there is no
 * client-side signal to finalize on. Those sessions predate chaining.
 */
export function sessionItemStatus(
  status: SigningSessionStatus | string,
  expiresAt: string | null,
  now: Date = new Date(),
  signers: ChainSigner[] = [],
  isSender = false
): SigningItemStatus {
  switch (status) {
    case "completed":
      return "signed";
    case "failed":
      return "failed";
    case "cancelled":
      return "cancelled";
    case "expired":
      return "expired";
    case "creating":
    case "awaiting_user": {
      // Ahead of the expiry check on purpose, and matching the backend: a chain
      // with nobody left to sign never expires (`expireChain` defers it
      // indefinitely) — the signatures it has exist and cannot be un-made, so
      // the deadline is spent. Reading it as "Expired" would claim the opposite.
      // `signed > 0` keeps out the shapes that have nobody pending but no
      // signature either: a legacy session with no signer rows, a chain whose
      // signers were all removed, and one every signer declined.
      //
      // It does NOT keep out a PARTIAL decline — one signature, one refusal,
      // nobody pending — and that is deliberate rather than missed. A rejection
      // voids the chain server-side (`computeChainAdvance` returns `failed` the
      // moment any signer is rejected), so this is a window of seconds before a
      // reconcile records it, and reaching `finalizing` is what CLOSES that
      // window: it is the state useSigningAutoRefresh drives
      // `dokobit-sign-status` on, and that call is the reconcile. Excluding it
      // would leave the row on "Waiting for others" — no less wrong, driven by
      // nothing, and lingering until the hourly sweep. Cancel stays offered
      // throughout (canStillCancel), and the panel does NOT tell that chain
      // "every signature is in" — the copy asks the SIGNERS rather than the
      // status (`finalizingAfterDecline`), so nothing here promises a container
      // the backend may never file.
      if (signerProgress(signers).signed > 0 && currentPosition(signers) === null) {
        return "finalizing";
      }
      const expiry = expiresAt ? Date.parse(expiresAt) : NaN;
      if (Number.isFinite(expiry) && expiry < now.getTime()) return "expired";
      const waitingForViewer = signers.length === 0 ? isSender : isWaitingForYou(signers);
      return waitingForViewer ? "awaiting_you" : "awaiting_others";
    }
    default:
      return "unknown";
  }
}

/** Dokobit's `signing_option` → the translation key for its display name. */
export type SigningMethodKey = "smartId" | "mobileId" | "idCard" | "unknown";

export function signingMethodKey(method: string | null | undefined): SigningMethodKey {
  switch ((method ?? "").toLowerCase()) {
    case "smartid":
      return "smartId";
    case "mobile":
    case "mobileid":
      return "mobileId";
    case "stationary":
      return "idCard";
    default:
      return "unknown";
  }
}

export interface SigningFilterValue {
  statuses: SigningItemStatus[];
  signerUserIds: number[];
  /** `yyyy-MM-dd`, inclusive. */
  from: string | null;
  to: string | null;
}

export const EMPTY_SIGNING_FILTER: SigningFilterValue = {
  statuses: [],
  signerUserIds: [],
  from: null,
  to: null,
};

export function isSigningFilterActive(value: SigningFilterValue): boolean {
  return (
    value.statuses.length > 0 ||
    value.signerUserIds.length > 0 ||
    value.from !== null ||
    value.to !== null
  );
}

/**
 * `Date.parse` that never yields NaN — `documents.created_at` is nullable, so
 * a missing date must sort as "oldest" instead of poisoning the comparator
 * (a NaN-returning comparator makes the whole sort implementation-defined).
 */
export function parseTimeOrZero(iso: string): number {
  const time = Date.parse(iso);
  return Number.isNaN(time) ? 0 : time;
}

/** The `yyyy-MM-dd` day an ISO timestamp falls on, in the viewer's timezone. */
function localDay(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const month = `${d.getMonth() + 1}`.padStart(2, "0");
  const day = `${d.getDate()}`.padStart(2, "0");
  return `${d.getFullYear()}-${month}-${day}`;
}

export interface SigningQuery {
  search: string;
  filter: SigningFilterValue;
  /** "My signatures" tab — keep only rows this user signed or started. */
  mineOnly: boolean;
  currentUserId: number | null;
}

type Predicate = (item: SigningListItem) => boolean;

/** Everyone this row involves: the sender plus every signer of the chain. */
function participantIds(item: SigningListItem): number[] {
  const ids = item.signers
    .map((s) => s.userId)
    .filter((id): id is number => id != null)
    .concat(item.senderUserId == null ? [] : [item.senderUserId]);
  return ids;
}

/**
 * "My signatures" — I started this chain, or I am one of its signers. Both
 * halves are real predicates now: `signing_session_signers.user_id` and
 * `document_signatures.signer_user_id` both carry profile ids, so a chain
 * somebody else sent me is mine too.
 */
function ownedBy(userId: number | null): Predicate {
  return (item) => userId != null && participantIds(item).includes(userId);
}

function hasOneOfStatuses(statuses: SigningItemStatus[]): Predicate {
  return (item) => statuses.includes(item.status);
}

/** The "Signed by" filter — many-to-many over the participants. */
function signedByOneOf(userIds: number[]): Predicate {
  return (item) => participantIds(item).some((id) => userIds.includes(id));
}

function createdWithin(from: string | null, to: string | null): Predicate {
  return (item) => {
    const day = localDay(item.createdAt);
    // `documents.created_at` is nullable — an undated row stays visible under
    // a date filter, matching the sort path's "don't vanish" contract
    // (parseTimeOrZero) rather than silently dropping a row that exists.
    if (!day) return true;
    return (!from || day >= from) && (!to || day <= to);
  };
}

function matchesText(needle: string): Predicate {
  return (item) =>
    [item.title, item.senderName, ...item.signers.map((s) => s.name)]
      .join(" ")
      .toLowerCase()
      .includes(needle);
}

/** The predicates the current query implies — an empty dimension adds none. */
function queryPredicates({ search, filter, mineOnly, currentUserId }: SigningQuery): Predicate[] {
  const needle = search.trim().toLowerCase();
  const predicates: Array<Predicate | null> = [
    mineOnly ? ownedBy(currentUserId) : null,
    filter.statuses.length > 0 ? hasOneOfStatuses(filter.statuses) : null,
    filter.signerUserIds.length > 0 ? signedByOneOf(filter.signerUserIds) : null,
    filter.from || filter.to ? createdWithin(filter.from, filter.to) : null,
    needle ? matchesText(needle) : null,
  ];
  return predicates.filter((p): p is Predicate => p !== null);
}

export function filterSigningItems(
  items: SigningListItem[],
  query: SigningQuery
): SigningListItem[] {
  const predicates = queryPredicates(query);
  return items.filter((item) => predicates.every((matches) => matches(item)));
}

/**
 * The row the detail sheet shows: the clicked one while it is still in
 * `items`. Nothing is picked on the user's behalf — the sheet covers part of
 * the list, so it opens only when a row is pressed, and never jumps to another.
 */
export function resolveSelection(
  items: SigningListItem[],
  selectedKey: string | null
): SigningListItem | null {
  return items.find((item) => item.key === selectedKey) ?? null;
}

/** One list from the two queries that feed the page, newest first. */
export function mergeSigningItems(
  sessions: SigningListItem[] | undefined,
  containers: SigningListItem[] | undefined
): SigningListItem[] {
  return [...(sessions ?? []), ...(containers ?? [])].sort(
    (a, b) => parseTimeOrZero(b.createdAt) - parseTimeOrZero(a.createdAt)
  );
}

export type SigningSortField = "title" | "sender" | "created";
export interface SigningSort {
  field: SigningSortField;
  direction: "asc" | "desc";
}

export const DEFAULT_SIGNING_SORT: SigningSort = { field: "created", direction: "desc" };

export function sortSigningItems(items: SigningListItem[], sort: SigningSort): SigningListItem[] {
  const factor = sort.direction === "asc" ? 1 : -1;
  return [...items].sort((a, b) => {
    if (sort.field === "created") {
      return (parseTimeOrZero(a.createdAt) - parseTimeOrZero(b.createdAt)) * factor;
    }
    const left = sort.field === "title" ? a.title : a.senderName;
    const right = sort.field === "title" ? b.title : b.senderName;
    return left.localeCompare(right) * factor;
  });
}

/**
 * Whether a row can join the multi-selection. Only a finished container is a
 * document — share, download, verify and Ask Franklin all act on one. A session
 * has no file until its chain completes, so there is nothing to act on yet.
 */
export function isBulkSelectable(item: SigningListItem): boolean {
  return item.kind === "container" && item.documentId != null;
}

/** "1 signature" / "3 signatures", from the i18n singular/plural pair. */
export function signatureCountLabel(count: number, copy: { one: string; many: string }): string {
  return (count === 1 ? copy.one : copy.many).replace("{count}", String(count));
}

/**
 * The distinct people present in the list — feeds the "Signed by" filter.
 *
 * Senders AND signers, because {@link signedByOneOf} matches on both: a chain
 * you were invited to must be reachable from the filter, not just the ones you
 * started. A later, non-empty name wins over a blank one — a session whose
 * `user_profiles` embed came back empty must not put a nameless chip in the
 * filter when another row names the same person.
 */
export function signersOf(items: SigningListItem[]): Array<{ id: number; name: string }> {
  const byId = new Map<number, string>();
  const remember = (id: number | null, name: string) => {
    if (id == null) return;
    if (!byId.has(id) || (!byId.get(id) && name)) byId.set(id, name);
  };
  for (const item of items) {
    remember(item.senderUserId, item.senderName);
    for (const signer of item.signers) remember(signer.userId, signer.name);
  }
  return [...byId.entries()]
    .map(([id, name]) => ({ id, name }))
    .sort((a, b) => a.name.localeCompare(b.name));
}
